import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Container from './ui/Container';
import { ArrowUpRight, MailIcon } from './ui/Icons';
import { projects } from '../data/projects';
import { CONTACT_EMAIL } from '../data/site';

const FooterRoot = styled.footer`
  background: var(--bg-0);
  border-top: 1px solid var(--border);
  padding: clamp(3.5rem, 7vw, 5rem) 0 2rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr 1.2fr 1.3fr;
  gap: 2.5rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Brand = styled.div`
  @media (max-width: 960px) {
    grid-column: 1 / -1;
  }
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 44px;
  margin-bottom: 1rem;

  img:first-child {
    height: 30px;
    width: auto;
  }

  img:last-child {
    height: 18px;
    width: auto;
  }
`;

const Description = styled.p`
  color: var(--text-2);
  font-size: 0.95rem;
  max-width: 36ch;
`;

const ColumnTitle = styled.h3`
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-3);
  margin-bottom: 0.75rem;
`;

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
`;

const linkStyles = `
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 40px;
  color: var(--text-2);
  font-size: 0.95rem;
  transition: color 0.18s ease;

  &:hover {
    color: var(--text-1);
  }
`;

const FooterLink = styled(Link)`
  ${linkStyles}
`;

const FooterAnchor = styled.a`
  ${linkStyles}
  word-break: break-word;
`;

const StatusText = styled.span`
  font-size: 0.75rem;
  color: var(--text-3);
`;

const Bottom = styled.div`
  margin-top: clamp(2.5rem, 5vw, 3.5rem);
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  color: var(--text-3);
  font-size: var(--fs-small);
`;

const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <FooterRoot>
      <Container>
        <Grid>
          <Brand>
            <LogoLink to="/" aria-label="CuttingEdge DevOps home">
              <img src="/images/LogoPlain.webp" alt="" width={128} height={79} />
              <img src="/images/logo.webp" alt="" width={640} height={96} />
            </LogoLink>
            <Description>
              A software studio in Puerto Rico. We design, build, secure, and maintain websites, mobile apps, and
              cloud platforms, one team from the first call to production.
            </Description>
          </Brand>

          <nav aria-labelledby="footer-company">
            <ColumnTitle id="footer-company">Company</ColumnTitle>
            <LinkList>
              <li>
                <FooterLink to="/services">Services</FooterLink>
              </li>
              <li>
                <FooterLink to="/projects">Work</FooterLink>
              </li>
              <li>
                <FooterLink to="/blog">Blog</FooterLink>
              </li>
              <li>
                <FooterLink to="/about">About</FooterLink>
              </li>
              <li>
                <FooterLink to="/contact">Contact</FooterLink>
              </li>
            </LinkList>
          </nav>

          <nav aria-labelledby="footer-work">
            <ColumnTitle id="footer-work">Work</ColumnTitle>
            <LinkList>
              {projects.map((project) => (
                <li key={project.slug}>
                  {project.status === 'live' && project.url ? (
                    <FooterAnchor href={project.url} target="_blank" rel="noopener noreferrer">
                      {project.name}
                      <ArrowUpRight size={14} />
                      <span className="visually-hidden">(opens in a new tab)</span>
                    </FooterAnchor>
                  ) : (
                    <FooterLink to={`/projects#${project.slug}`}>
                      {project.name} <StatusText>· Coming soon</StatusText>
                    </FooterLink>
                  )}
                </li>
              ))}
            </LinkList>
          </nav>

          <div>
            <ColumnTitle>Contact</ColumnTitle>
            <LinkList>
              <li>
                <FooterAnchor href={`mailto:${CONTACT_EMAIL}`}>
                  <MailIcon size={16} />
                  {CONTACT_EMAIL}
                </FooterAnchor>
              </li>
              <li>
                <FooterLink to="/contact">Send a message</FooterLink>
              </li>
            </LinkList>
          </div>
        </Grid>

        <Bottom>
          <span>© {year} CuttingEdge DevOps. All rights reserved.</span>
          <span>Puerto Rico · Working remotely</span>
        </Bottom>
      </Container>
    </FooterRoot>
  );
};

export default Footer;
